/**
 * Utility functions for the Leap CMS components and modules
 */

/**
 * Checks if a string value is non-empty after trimming
 *
 * @param {string} value - String value to check
 * @returns {boolean} True if the string is non-empty
 *
 * @example
 * hasValidString('Hello') // true
 * hasValidString('') // false
 * hasValidString('   ') // false
 * hasValidString(null) // false
 */
export const hasValidString = (value) => {
    return typeof value === 'string' && value.trim() !== ''
}

/**
 * Checks if a video source has a valid src
 *
 * @param {Object} source - Video source object
 * @param {string} source.src - Video source URL
 * @returns {boolean} True if the source has a valid src
 *
 * @example
 * hasValidVideoSource({src: 'video.mp4'}) // true
 * hasValidVideoSource({src: ''}) // false
 * hasValidVideoSource(null) // false
 */
export const hasValidVideoSource = (source) => {
    return !!(source && hasValidString(source.src))
}

/**
 * Checks if a video asset has valid content that can be rendered
 *
 * @param {Object} asset - Video asset object
 * @param {string} asset.type - Type of video: 'direct' or 'brightcove'
 * @param {string} [asset.brightcoveId] - Brightcove video ID (for brightcove type)
 * @param {Array} [asset.sources] - Array of video sources (for direct type)
 * @returns {boolean} True if the video asset has valid content
 *
 * @example
 * // Brightcove video
 * hasValidVideoContent({type: 'brightcove', brightcoveId: '12345'}) // true
 * hasValidVideoContent({type: 'brightcove', brightcoveId: ''}) // false
 *
 * @example
 * // Direct video
 * hasValidVideoContent({
 *   type: 'direct',
 *   sources: [{src: 'video.mp4', type: 'video/mp4'}]
 * }) // true
 * hasValidVideoContent({
 *   type: 'direct',
 *   sources: [{src: '', type: 'video/mp4'}]
 * }) // false
 */
export const hasValidVideoContent = (asset) => {
    if (!asset) return false

    if (asset.type === 'brightcove') {
        return hasValidString(asset.brightcoveId)
    }

    if (asset.type === 'direct') {
        return !!(
            asset.sources &&
            Array.isArray(asset.sources) &&
            asset.sources.some(hasValidVideoSource)
        )
    }

    return false
}

/**
 * Checks if an image asset has a valid source URL
 *
 * @param {Object} asset - Image asset object
 * @param {string} asset.src - Image source URL
 * @returns {boolean} True if the image has a valid source
 *
 * @example
 * hasValidImageSrc({src: 'image.jpg'}) // true
 * hasValidImageSrc({src: ''}) // false
 * hasValidImageSrc({src: '   '}) // false
 */
export const hasValidImageSrc = (asset) => {
    return hasValidString(asset?.src)
}

/**
 * Checks if an asset is valid video or image asset
 *
 * @param {Object} asset - asset object
 * @returns {boolean} True if the asset is a valid source
 */
export const hasValidAsset = (asset) => {
    return hasValidVideoContent(asset) || hasValidImageSrc(asset)
}
/**
 * Checks if a price value is valid for display
 * A valid price is a number (including 0) but not null, undefined, empty string, or NaN
 *
 * @param {number|string|null|undefined} value - Price value to check
 * @returns {boolean} True if the price value is valid for display
 *
 * @example
 * hasValidPrice(100) // true
 * hasValidPrice(0) // true
 * hasValidPrice('100') // false (string numbers are not valid)
 * hasValidPrice(null) // false
 * hasValidPrice(undefined) // false
 * hasValidPrice('') // false
 * hasValidPrice(NaN) // false
 */
export const hasValidPrice = (value) => {
    return typeof value === 'number' && !isNaN(value)
}

/**
 * Checks if an asset is a video type (direct or brightcove)
 *
 * @param {Object} asset - Asset object
 * @param {string} asset.type - Asset type
 * @returns {boolean} True if the asset is a video type
 *
 * @example
 * isVideoAsset({type: 'direct'}) // true
 * isVideoAsset({type: 'brightcove'}) // true
 * isVideoAsset({type: 'image'}) // false
 */
export const isVideoAsset = (asset) => {
    return asset?.type === 'direct' || asset?.type === 'brightcove'
}

/**
 * Resolves the effective video type for an asset.
 * Direct videos are always 'animation'. Brightcove videos default to 'animation'
 * unless explicitly set to 'video'.
 *
 * @param {Object} asset - Asset object
 * @param {string} [asset.type] - Asset type ('direct' or 'brightcove')
 * @param {string} [asset.videoType] - Video type ('animation' or 'video')
 * @returns {'animation'|'video'} The effective video type
 *
 * @example
 * resolveVideoType({type: 'direct'}) // 'animation'
 * resolveVideoType({type: 'brightcove'}) // 'animation'
 * resolveVideoType({type: 'brightcove', videoType: 'video'}) // 'video'
 * resolveVideoType({type: 'brightcove', videoType: 'animation'}) // 'animation'
 * resolveVideoType(null) // null
 */
export const resolveVideoType = (asset) => {
    if (!asset) return null
    if (asset.type === 'direct') return 'animation'
    if (asset.type === 'brightcove' && asset.videoType === 'video') return 'video'
    return 'animation'
}

/**
 * Checks if an asset should use "full video" behavior (stopped by default,
 * native controls, poster-based play button).
 *
 * @param {Object} asset - Asset object
 * @returns {boolean} True if the asset should use full video behavior
 *
 * @example
 * isFullVideo({type: 'brightcove', videoType: 'video'}) // true
 * isFullVideo({type: 'brightcove'}) // false
 * isFullVideo({type: 'direct', videoType: 'video'}) // false (direct is always animation)
 */
export const isFullVideo = (asset) => resolveVideoType(asset) === 'video'

/**
 * Normalizes a string value for comparison
 * Converts whitespace-only strings to empty string, trims non-empty strings
 * Converts null and undefined to undefined
 *
 * @param {string} value - String value to normalize
 * @returns {string|undefined} Normalized string or undefined
 *
 * @example
 * normalizeString('Hello') // 'Hello'
 * normalizeString('  Hello  ') // 'Hello'
 * normalizeString('') // ''
 * normalizeString('   ') // ''
 * normalizeString(null) // undefined
 * normalizeString(undefined) // undefined
 */
export const normalizeString = (value) => {
    if (typeof value !== 'string') return undefined
    const trimmed = value.trim()
    return trimmed === '' ? '' : trimmed
}

/**
 * Normalizes a string value for comparison, treating empty/whitespace as undefined
 * Used for optional fields where empty should be treated as missing
 *
 * @param {string} value - String value to normalize
 * @returns {string|undefined} Normalized string or undefined
 *
 * @example
 * normalizeOptionalString('Hello') // 'Hello'
 * normalizeOptionalString('  Hello  ') // 'Hello'
 * normalizeOptionalString('') // undefined
 * normalizeOptionalString('   ') // undefined
 */
export const normalizeOptionalString = (value) => {
    if (typeof value !== 'string') return undefined
    const trimmed = value.trim()
    return trimmed === '' ? undefined : trimmed
}

/**
 * Normalizes a boolean value for comparison
 * Converts undefined, null to false
 *
 * @param {boolean} value - Boolean value to normalize
 * @returns {boolean} Normalized boolean (defaults to false)
 *
 * @example
 * normalizeBoolean(true) // true
 * normalizeBoolean(false) // false
 * normalizeBoolean(undefined) // false
 * normalizeBoolean(null) // false
 */
export const normalizeBoolean = (value) => {
    return value === true
}

/**
 * Maps a `c_showFloatingHeader` custom attribute value (as returned by the API,
 * where booleans may arrive as the string `'true'`/`'false'`) to a `HeaderVariant`
 * variant.
 *
 * @param {boolean|string} [showFloatingHeader] - The raw `c_showFloatingHeader` value.
 * @returns {'inline'|'floating'}
 */
export const getFloatingHeaderVariant = (showFloatingHeader) =>
    showFloatingHeader === true || showFloatingHeader === 'true' ? 'floating' : 'inline'

/**
 * Normalizes a video source object for comparison
 * Filters out sources with empty src, normalizes string fields
 *
 * @param {Object} source - Video source object
 * @param {string} source.src - Video source URL
 * @param {string} [source.type] - MIME type
 * @param {string} [source.breakpoint] - Responsive breakpoint
 * @returns {Object|null} Normalized source or null if src is empty
 *
 * @example
 * normalizeVideoSource({src: 'video.mp4', type: 'video/mp4'})
 * // {src: 'video.mp4', type: 'video/mp4'}
 * normalizeVideoSource({src: '  ', type: 'video/mp4'}) // null
 */
export const normalizeVideoSource = (source) => {
    if (!source) return null
    const src = normalizeOptionalString(source.src)
    if (!src) return null

    return {
        src,
        ...(source.type && {type: normalizeOptionalString(source.type)}),
        ...(source.breakpoint && {breakpoint: normalizeOptionalString(source.breakpoint)})
    }
}

/**
 * Normalizes an asset object for comparison
 * Handles different asset types (image, direct video, brightcove video)
 * Removes insignificant differences like whitespace
 *
 * @param {Object} asset - Asset object to normalize
 * @returns {Object|null} Normalized asset or null if asset is invalid
 *
 * @example
 * // Image asset
 * normalizeAsset({
 *   type: 'image',
 *   src: '  image.jpg  ',
 *   alt: '  ',
 * })
 * // {type: 'image', src: 'image.jpg', alt: ''}
 *
 * @example
 * // Direct video asset
 * normalizeAsset({
 *   type: 'direct',
 *   sources: [{src: 'video.mp4', type: 'video/mp4'}],
 *   autoplay: undefined,
 *   videoControls: false
 * })
 * // {type: 'direct', sources: [{src: 'video.mp4', type: 'video/mp4'}], autoplay: false, videoControls: false}
 */
export const normalizeAsset = (asset) => {
    if (!asset || !asset.type) return null

    const normalized = {
        type: asset.type
    }

    // Common fields
    const title = normalizeOptionalString(asset.title)
    if (title !== undefined) normalized.title = title

    // Type-specific normalization
    if (asset.type === 'image') {
        const src = normalizeOptionalString(asset.src)
        if (!src) return null // Image without src is invalid

        normalized.src = src

        // alt can be empty string (valid for decorative images)
        // Only normalize whitespace-only to empty string
        const alt = normalizeString(asset.alt)
        if (alt !== undefined) normalized.alt = alt

        const disBaseLink = normalizeOptionalString(asset.disBaseLink)
        if (disBaseLink !== undefined) normalized.disBaseLink = disBaseLink
    } else if (asset.type === 'brightcove') {
        const brightcoveId = normalizeOptionalString(asset.brightcoveId)
        if (!brightcoveId) return null // Brightcove without ID is invalid

        normalized.brightcoveId = brightcoveId
        normalized.autoplay = normalizeBoolean(asset.autoplay)
        normalized.videoControls = normalizeBoolean(asset.videoControls)

        // Passthrough videoType to support future video types
        const videoType = normalizeOptionalString(asset.videoType)
        if (videoType) normalized.videoType = videoType

        if (asset.poster && typeof asset.poster === 'object') {
            const posterSrc = normalizeOptionalString(asset.poster.src)
            const posterDisBaseLink = normalizeOptionalString(asset.poster.disBaseLink)

            if (posterSrc || posterDisBaseLink) {
                normalized.poster = {}
                if (posterSrc) normalized.poster.src = posterSrc
                if (posterDisBaseLink) normalized.poster.disBaseLink = posterDisBaseLink

                const posterAlt = normalizeString(asset.poster.alt)
                if (posterAlt !== undefined) normalized.poster.alt = posterAlt
            }
        }
    } else if (asset.type === 'direct') {
        if (!asset.sources || !Array.isArray(asset.sources)) return null

        // Normalize and filter sources
        const normalizedSources = asset.sources
            .map(normalizeVideoSource)
            .filter(Boolean)
            .sort((a, b) => {
                // Sort by breakpoint for consistent comparison
                const bpA = a.breakpoint || ''
                const bpB = b.breakpoint || ''
                return bpA.localeCompare(bpB)
            })

        if (normalizedSources.length === 0) return null // Direct video without valid sources is invalid

        normalized.sources = normalizedSources
        normalized.autoplay = normalizeBoolean(asset.autoplay)
        normalized.videoControls = normalizeBoolean(asset.videoControls)

        // Direct video poster only supports src (not disBaseLink)
        if (asset.poster && typeof asset.poster === 'object') {
            const posterSrc = normalizeOptionalString(asset.poster.src)
            if (posterSrc) {
                normalized.poster = {src: posterSrc}
                const posterAlt = normalizeString(asset.poster.alt)
                if (posterAlt !== undefined) normalized.poster.alt = posterAlt
            }
        }
    }

    return normalized
}

// parse aspectRatio from momentvm to correct chakra ui value
export const parseAspectRatio = (ratioStr) => {
    const [w, h] = ratioStr.split('/').map(Number)
    return w / h
}

/**
 * Sanitizes a string to be used as an HTML ID attribute
 * Removes hash prefix, converts to lowercase, replaces spaces and special characters with hyphens
 * Ensures ID starts with a letter by prepending 'anchor-' if needed
 *
 * @param {string} value - ID value to sanitize
 * @returns {string|undefined} Sanitized ID or undefined if invalid
 *
 * @example
 * sanitizeAnchorId('My Section') // 'my-section'
 * sanitizeAnchorId('#section-1') // 'section-1'
 * sanitizeAnchorId('section!@#$') // 'section'
 * sanitizeAnchorId('123section') // 'anchor-123section'
 * sanitizeAnchorId('') // undefined
 */
export const sanitizeAnchorId = (value) => {
    if (!hasValidString(value)) return undefined

    // Remove hash prefix(es), trim, and convert to lowercase
    let sanitized = value.trim().replace(/^#+/, '').toLowerCase()

    // Replace spaces and special characters with hyphens
    // Keep only alphanumeric, hyphens, and underscores
    sanitized = sanitized.replace(/[^a-z0-9_-]/g, '-')

    // Replace multiple consecutive hyphens with single hyphen
    sanitized = sanitized.replace(/-+/g, '-')

    // Remove leading/trailing hyphens
    sanitized = sanitized.replace(/^-+|-+$/g, '')

    // Return undefined if nothing is left after sanitization
    if (!sanitized) return undefined

    // Ensure ID starts with a letter (HTML requirement)
    if (!/^[a-z]/.test(sanitized)) {
        sanitized = `anchor-${sanitized}`
    }

    return sanitized
}

/**
 * Sanitizes a string to be used as an anchor target (href)
 * Ensures hash prefix, converts to lowercase, replaces spaces and special characters with hyphens
 *
 * @param {string} value - Target value to sanitize
 * @returns {string|undefined} Sanitized target with hash prefix or undefined if invalid
 *
 * @example
 * sanitizeAnchorTarget('my-section') // '#my-section'
 * sanitizeAnchorTarget('#section-1') // '#section-1'
 * sanitizeAnchorTarget('My Section') // '#my-section'
 * sanitizeAnchorTarget('') // undefined
 */
export const sanitizeAnchorTarget = (value) => {
    const sanitizedId = sanitizeAnchorId(value)
    return sanitizedId ? `#${sanitizedId}` : undefined
}

/**
 * Checks if a darken layer is turned off (0%, none, or missing/falsy)
 *
 * @param {string} darken - Darken overlay value (e.g. '0%', '40%', 'none')
 * @returns {boolean} True if darken is off or not applied
 *
 * @example
 * isDarkenOff('0%') // true
 * isDarkenOff('0') // true
 * isDarkenOff('none') // true
 * isDarkenOff('') // true
 * isDarkenOff(null) // true
 * isDarkenOff(undefined) // true
 * isDarkenOff('40%') // false
 */
export const isDarkenOff = (darken) => {
    return !hasValidString(darken) || darken === '0%' || darken === '0' || darken === 'none'
}

/**
 * Normalizes a darken value to a standard percentage string
 *
 * @param {string} darken - Darken value (e.g. '0%', '40', 'none')
 * @returns {string} Normalized darken percentage ('0%', '40%', etc.)
 *
 * @example
 * normalizeDarken('0%') // '0%'
 * normalizeDarken('none') // '0%'
 * normalizeDarken(null) // '0%'
 * normalizeDarken('40') // '40%'
 * normalizeDarken('40%') // '40%'
 */
export const normalizeDarken = (darken) => {
    if (isDarkenOff(darken)) {
        return '0%'
    }
    return darken.includes('%') ? darken : `${darken}%`
}
